import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { Icon } from '../../components/ui/Icon';
import { ErrorNote } from '../../components/ui/misc';
import { ApiError } from '../../lib/api/errors';
import { authApi } from './api';
import { AuthLayout } from './LoginPage';

/**
 * FR-ACC-003 entry point — new in Stage 4 Phase 1 (no reference screen for
 * the request step itself; `01a-password-reset` only covers the token-in-
 * hand "set new password" step). `requestPasswordReset` is
 * enumeration-resistant at the API boundary (always `{ status: 'ok' }`), so
 * this page shows the identical confirmation state whether or not the
 * email actually has an account — never reveal which via timing or copy.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<{ message: string; tone: 'alert' | 'accent' } | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setError({ message: 'Too many reset requests. Wait a while and try again.', tone: 'accent' });
      } else {
        setError({ message: 'Could not reach the server. Check your connection and try again.', tone: 'alert' });
      }
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout>
        <div className="mb-2 flex justify-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-positive-soft text-positive">
            <Icon name="mark_email_read" className="text-[22px]" />
          </div>
        </div>
        <h1 className="text-center text-xl font-semibold text-ink">Check your email</h1>
        <p className="mt-2 text-center text-sm text-ink-muted">
          If an account exists for <span className="font-mono text-xs font-medium text-ink">{email.trim()}</span>, a
          password reset link has been sent. The link is valid for a limited time and can only be used once.
        </p>
        <div className="mt-6 border-t border-sunken pt-4 text-center">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink">
            <Icon name="arrow_back" className="text-[15px]" />
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="text-xl font-semibold text-ink">Reset your password</h1>
      <p className="mt-1 text-sm text-ink-muted">
        Enter your work email and we'll send you a link to set a new password.
      </p>
      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
        {error && <ErrorNote tone={error.tone}>{error.message}</ErrorNote>}
        <Field
          label="Email"
          type="email"
          autoComplete="username"
          required
          icon={<Icon name="mail" className="text-[18px]" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button type="submit" loading={busy} className="mt-1 w-full">
          <span>Send reset link</span>
          <Icon name="arrow_forward" className="text-[18px]" />
        </Button>
      </form>
      <div className="mt-5 text-center">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-hover">
          <Icon name="arrow_back" className="text-[15px]" />
          Return to sign in
        </Link>
      </div>
    </AuthLayout>
  );
}
